import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import style from "./DocumentsTab.module.scss";
import { File, Driver } from "../../../../../services";
import Loading from "../../../../shared/Loading/Loading";
import DocumentNewIcon from "../../../../shared/icons/DocumentNewIcon";
import UnloadIcon from "../../../../shared/icons/UnloadIcon";
import DeleteNewIcon from "../../../../shared/icons/DeleteNewIcon";
import UploadDocumentModal from "../UploadDocumentModal/UploadDocumentModal";
import ConfirmActionModal from "../ConfirmActionModal/ConfirmActionModal";
import EyeIcon from "../../../../shared/icons/EyeIcon";
import DocumentPreviewModal from "../../../../shared/DocumentPreviewModal/DocumentPreviewModal";
import DocumentUploadSubtitle from "../../../../shared/DocumentUploadSubtitle/DocumentUploadSubtitle";
import {
  canPreviewDocument,
  isImageDocument,
  isPdfDocument,
} from "../../../../../utils/documentPreviewUtils";

const formatDocumentType = (contentType) => {
  const mimeMap = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };
  return mimeMap[contentType] || contentType?.split("/")[1];
};

const getDriverDocTitle = (doc, driverName, vehicleInfo, t) => {
  if (doc.type === 3 && doc.side === 0) {
    return t("orderInfoExpeditor.documentsTab.driverIdFront", { driverName });
  }
  if (doc.type === 3 && doc.side === 1) {
    return t("orderInfoExpeditor.documentsTab.driverIdBack", { driverName });
  }
  if (doc.type === 6 && doc.side === 0) {
    return t("orderInfoExpeditor.documentsTab.driverLicenseFront", { driverName });
  }
  if (doc.type === 6 && doc.side === 1) {
    return t("orderInfoExpeditor.documentsTab.driverLicenseBack", { driverName });
  }
  if (doc.type === 4 && doc.side === 0) {
    return t("orderInfoExpeditor.documentsTab.trailerRegistration", {
      vehicleName: vehicleInfo?.carModel || "—",
    });
  }
  if (doc.type === 7 && doc.side === 0) {
    return t("orderInfoExpeditor.documentsTab.vehicleRegistration", {
      vehicleName: vehicleInfo?.trailerModel || "—",
    });
  }
  return doc.typeName || "";
};

const DocumentsTab = ({ order, listFilesOrder = [], refreshDocuments }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);

  const [loadingDownload, setLoadingDownload] = useState({});
  const [loadingPreview, setLoadingPreview] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cachedFiles, setCachedFiles] = useState({});
  const cachedFilesRef = useRef({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [driverDocs, setDriverDocs] = useState([]);
  const [showConfirmDownloadModal, setShowConfirmDownloadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const drivers = order?.drivers || [];
  const driversKey = drivers.map((d) => d.driverId).join(",");

  useEffect(() => {
    return () => {
      Object.values(cachedFilesRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  useEffect(() => {
    if (!drivers.length) {
      setDriverDocs([]);
      return;
    }

    const fetchAll = async () => {
      const results = [];

      for (const { driverId, vehicleId } of drivers) {
        let driverInfo = null;
        let vehicleInfo = null;

        try {
          const r = await Driver.getDriverInfo(driverId);
          if (r.status === 200) driverInfo = r.data || {};
        } catch {}

        if (vehicleId) {
          try {
            const r = await Driver.getVehicleInfo(vehicleId);
            if (r.status === 200) vehicleInfo = r.data || {};
          } catch {}
        }

        const driverName = driverInfo
          ? [driverInfo.surname, driverInfo.name, driverInfo.patronymic].filter(Boolean).join(" ")
          : "—";

        try {
          const r = await File.getDocList(driverId);
          const filtered = (r.data || []).filter((d) => d.type !== 8);
          const hasTypes = [4, 5, 7].some((type) => filtered.some((d) => d.type === type));

          let vehicleDocs = [];
          if (!hasTypes && vehicleId) {
            try {
              const vr = await File.getDocListVehicle(vehicleId);
              vehicleDocs = vr.data || [];
            } catch {}
          }

          [...filtered, ...vehicleDocs].forEach((doc) => {
            results.push({
              ...doc,
              _isDriverDoc: true,
              _title: getDriverDocTitle(doc, driverName, vehicleInfo, t),
            });
          });
        } catch {}
      }

      setDriverDocs(results);
    };

    fetchAll();
  }, [driversKey, t]);

  const allDocs = [...listFilesOrder, ...driverDocs];

  const getStageLabel = (addressId) => {
    if (!addressId || !order?.addressList) return null;
    const addr = order.addressList.find((a) => a.id === addressId);
    if (!addr) return null;
    if (addr.type === "Loading") return t("orderInfo.documentsTab.stageLoading");
    if (addr.type === "Unloading") return t("orderInfo.documentsTab.stageUnloading");
    return addr.type;
  };

  const cacheFileUrl = (fileId, url) => {
    cachedFilesRef.current[fileId] = url;
    setCachedFiles((previous) => ({ ...previous, [fileId]: url }));
  };

  const requestFile = (doc) => {
    if (doc._isDriverDoc) return File.getDocById(doc.fileId);
    return File.downloadFileById(doc.fileId);
  };

  const downloadFile = (doc) => {
    if (!doc) return;
    const cache = cachedFiles[doc.fileId];
    setLoadingDownload((p) => ({ ...p, [doc.id]: true }));
    const finish = () => setLoadingDownload((p) => ({ ...p, [doc.id]: false }));
    if (cache) {
      const a = document.createElement("a");
      a.href = cache;
      a.setAttribute("download", doc.fileName || doc._title || "file");
      a.click();
      finish();
      return;
    }
    requestFile(doc)
      .then((resp) => {
        const blob = new Blob([resp.data], { type: doc.contentType || resp.data.type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", doc.fileName || doc._title || "file");
        a.click();
        cacheFileUrl(doc.fileId, url);
      })
      .catch(() => toast.error(t("orderInfo.listFilesOrder.downloadFileError")))
      .finally(finish);
  };

  const openFile = (doc, title) => {
    if (!doc || !canPreviewDocument(doc)) return;

    const cache = cachedFiles[doc.fileId];
    const docKey = doc.id || doc.fileId;
    const isPdfFile = isPdfDocument(doc);
    const pdfWindow = isPdfFile ? window.open("about:blank", "_blank") : null;

    if (isPdfFile && !pdfWindow) {
      toast.error(t("orderInfo.listFilesOrder.openFileError"));
      return;
    }

    if (pdfWindow) {
      pdfWindow.opener = null;
    }

    const showFile = (url) => {
      if (isPdfFile) {
        pdfWindow.location.href = url;
        return;
      }

      if (isImageDocument(doc)) {
        setPreviewImage({ url, title });
      }
    };

    if (cache) {
      showFile(cache);
      return;
    }

    setLoadingPreview((previous) => ({ ...previous, [docKey]: true }));
    requestFile(doc)
      .then((resp) => {
        const blob = new Blob([resp.data], {
          type: doc.contentType || resp.data?.type,
        });
        const url = URL.createObjectURL(blob);
        cacheFileUrl(doc.fileId, url);
        showFile(url);
      })
      .catch(() => {
        if (pdfWindow && !pdfWindow.closed) {
          pdfWindow.close();
        }
        toast.error(t("orderInfo.listFilesOrder.openFileError"));
      })
      .finally(() => {
        setLoadingPreview((previous) => ({ ...previous, [docKey]: false }));
      });
  };

  const openDeleteModal = (fileId) => {
    setDeletingId(fileId);
    setIsConfirmOpen(true);
  };

  const handleConfirmDownload = () => {
    const orderId = order?.id;
    if (!orderId) {
      toast.error(t('orderInfo.genericError'));
      setShowConfirmDownloadModal(false);
      return;
    }

    setIsLoading(true);
    File.downloadZipByOrderId(orderId)
      .then((response) => {
        const fileBlob = new Blob([response.data], { type: 'application/zip' });
        const fileURL = URL.createObjectURL(fileBlob);
        const downloadElement = document.createElement("a");
        downloadElement.href = fileURL;
        downloadElement.download = `order_${orderId}_documents.zip`;
        downloadElement.click();
        URL.revokeObjectURL(fileURL);
        setShowConfirmDownloadModal(false);
      })
      .catch((error) => {
        console.log("Произошла ошибка при скачивании архива", error);
        toast.error(t('orderInfo.genericError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConfirmDelete = () => {
    if (!deletingId) return;
    setIsDeleting(true);
    File.deleteDocumentOrders(deletingId)
      .then(() => {
        toast.success(t("orderInfo.listFilesOrder.deleteSuccess"));
        refreshDocuments && refreshDocuments();
      })
      .catch(() => toast.error(t("orderInfo.listFilesOrder.deleteError")))
      .finally(() => {
        setIsDeleting(false);
        setIsConfirmOpen(false);
        setDeletingId(null);
      });
  };

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <div className={style.header_title}>
          <span className={style.header_label}>{t("orderInfo.documentsTab.allDocuments")}</span>
          <span className={style.header_dot} />
          <span className={style.header_count}>
            {allDocs.length} {t("orderInfo.documentsTab.filesCount")}
          </span>
        </div>
        <div className={style.header_actions}>
          <button className={style.upload_btn} onClick={() => setIsUploadModalOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M6.66688 13.3332L10.0002 9.9999L13.3335 13.3332M10.0002 9.9999V17.4999M16.9919 15.3249C17.8047 14.8818 18.4467 14.1806 18.8168 13.3321C19.1868 12.4835 19.2637 11.5359 19.0354 10.6388C18.807 9.7417 18.2865 8.94616 17.5558 8.37778C16.8251 7.80939 15.9259 7.50052 15.0002 7.4999H13.9502C13.698 6.52427 13.2278 5.61852 12.5752 4.85073C11.9225 4.08295 11.1042 3.47311 10.182 3.06708C9.25967 2.66104 8.25734 2.46937 7.25031 2.50647C6.24328 2.54358 5.25777 2.80849 4.36786 3.28129C3.47795 3.7541 2.7068 4.42249 2.1124 5.23622C1.51799 6.04996 1.11579 6.98785 0.936028 7.9794C0.756269 8.97095 0.803632 9.99035 1.07456 10.961C1.34548 11.9316 1.83291 12.8281 2.50021 13.5832" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t("orderInfo.documentsTab.uploadDocument")}</span>
          </button>
          <button className={style.download_btn} onClick={() => setShowConfirmDownloadModal(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9.68717 12.9272C9.58995 12.8922 9.49967 12.833 9.41634 12.7497L6.41634 9.74968C6.24967 9.58301 6.16967 9.38857 6.17634 9.16634C6.18301 8.94412 6.26301 8.74968 6.41634 8.58301C6.58301 8.41634 6.78106 8.32968 7.01051 8.32301C7.23995 8.31634 7.43773 8.39607 7.60384 8.56218L9.16634 10.1247V4.16634C9.16634 3.93023 9.24634 3.73246 9.40634 3.57301C9.56634 3.41357 9.76412 3.33357 9.99967 3.33301C10.2352 3.33246 10.4333 3.41246 10.5938 3.57301C10.7544 3.73357 10.8341 3.93134 10.833 4.16634V10.1247L12.3955 8.56218C12.5622 8.39551 12.7602 8.31551 12.9897 8.32218C13.2191 8.32884 13.4169 8.41579 13.583 8.58301C13.7358 8.74968 13.8158 8.94412 13.823 9.16634C13.8302 9.38857 13.7502 9.58301 13.583 9.74968L10.583 12.7497C10.4997 12.833 10.4094 12.8922 10.3122 12.9272C10.215 12.9622 10.1108 12.9794 9.99967 12.9788C9.88856 12.9783 9.7844 12.9611 9.68717 12.9272ZM4.99967 16.6663C4.54134 16.6663 4.14912 16.5033 3.82301 16.1772C3.4969 15.8511 3.33356 15.4586 3.33301 14.9997V13.333C3.33301 13.0969 3.41301 12.8991 3.57301 12.7397C3.73301 12.5802 3.93079 12.5002 4.16634 12.4997C4.4019 12.4991 4.59995 12.5791 4.76051 12.7397C4.92106 12.9002 5.00079 13.098 4.99967 13.333V14.9997H14.9997V13.333C14.9997 13.0969 15.0797 12.8991 15.2397 12.7397C15.3997 12.5802 15.5975 12.5002 15.833 12.4997C16.0686 12.4991 16.2666 12.5791 16.4272 12.7397C16.5877 12.9002 16.6675 13.098 16.6663 13.333V14.9997C16.6663 15.458 16.5033 15.8505 16.1772 16.1772C15.8511 16.5038 15.4586 16.6669 14.9997 16.6663H4.99967Z" fill="#0D3B66"/>
            </svg>
            <span>{t("orderInfo.documentsTab.downloadAll")}</span>
          </button>
        </div>
      </div>

      <div className={style.list}>
        {allDocs.length === 0 && <div className={style.empty}>{t("orderInfo.documentsTab.empty")}</div>}
        {allDocs.map((doc) => {
          const isDriverDoc = !!doc._isDriverDoc;

          const formattedType = formatDocumentType(doc.contentType);
          const stage = !isDriverDoc && getStageLabel(doc.addressId);
          const subtitlePrefix = stage
            ? `${t("orderInfo.documentsTab.stage")} - ${stage}${t("orderInfoExpeditor.documentsTab.separator")}`
            : "";

          const title = isDriverDoc ? doc._title : doc.name || doc.fileName;
          const canDelete = !isDriverDoc && user?.companyId === doc.companyId && order?.status !== "completed";
          const docKey = doc.id || doc.fileId;

          return (
            <div key={doc.id} className={style.item}>
              <div className={style.item_icon}>
                <DocumentNewIcon />
              </div>
              <div className={style.item_info}>
                <span className={style.item_name}>{title}</span>
                <DocumentUploadSubtitle
                  document={doc}
                  companyId={user?.companyId}
                  uploadedText={t(
                    isDriverDoc
                      ? "orderInfoExpeditor.documentsTab.uploaded"
                      : "orderInfo.documentsTab.uploaded",
                  )}
                  formattedType={formattedType}
                  prefix={subtitlePrefix}
                  className={style.item_subtitle}
                />
              </div>
              {loadingDownload[doc.id] ? (
                <Loading inBlock marginBottom={0} />
              ) : (
                <button className={style.action_btn} onClick={() => downloadFile(doc)}>
                  <UnloadIcon type={2} />
                </button>
              )}
              {canPreviewDocument(doc) && (
                loadingPreview[docKey] ? (
                  <Loading inBlock marginBottom={0} />
                ) : (
                  <button
                    type="button"
                    className={style.action_btn}
                    onClick={() => openFile(doc, title)}
                    aria-label={`${t("orderInfo.ordersDriverInfo.viewDocsLabel")}: ${title}`}
                  >
                    <EyeIcon />
                  </button>
                )
              )}
              {canDelete && (
                <button className={style.action_btn} onClick={() => openDeleteModal(doc.fileId)}>
                  <DeleteNewIcon color="#6D7885" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <DocumentPreviewModal
        isOpen={Boolean(previewImage)}
        imageUrl={previewImage?.url}
        title={previewImage?.title}
        closeLabel={t("confirmDocs.buttonClose")}
        onClose={() => setPreviewImage(null)}
      />

      {isConfirmOpen && ReactDOM.createPortal(
        <div className={style.confirm_overlay} data-portal-modal="true">
          <div className={style.confirm_modal}>
            <p className={style.confirm_text}>{t("orderInfo.listFilesOrder.confirmDelete")}</p>
            <div className={style.confirm_actions}>
              <button
                className={cn(style.confirm_btn, style.confirm_btn_cancel)}
                onClick={() => {
                  setDeletingId(null);
                  setIsConfirmOpen(false);
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                className={cn(style.confirm_btn, style.confirm_btn_ok)}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? <Loading inBlock marginBottom={0} /> : t("common.confirm")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmActionModal
        isOpen={showConfirmDownloadModal}
        title={t("orderInfo.orderHistory.downloadAllTitle")}
        message={t("orderInfo.orderHistory.downloadAllMessage")}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        onConfirm={handleConfirmDownload}
        onCancel={() => setShowConfirmDownloadModal(false)}
        isLoading={isLoading}
      />

      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        order={order}
        refreshDocuments={refreshDocuments}
      />
    </div>
  );
};

export default DocumentsTab;
